import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { visuallyHidden } from "@mui/utils";
import ScrollEffect from "../../effects/ScrollEffect";
import { Download, Eye, FunnelSimple, Link, Pencil, PlusCircle, X } from "phosphor-react";
import Select from "../Inputs/Select";
import QueueStyle from "../Styled/Queue.style";
import {
  useModalHistory,
  ACTIONS as MODAL_ACTIONS,
} from "../../context/ModalHistoryProvider";
import UpdateDealModal from "../Modals/UpdateDeal/UpdateDealModal";
import LoadEffect from "../../effects/LoadEffect";
import { Badge, CircularProgress, Skeleton } from "@mui/material";
import StackStyle from "../Styled/Stack.style";
import useType from "../../utils/hooks/useType";
import Popup from "../Popup/Popup";
import { CancelButton } from "../Controls/CancelButton";
import { ConfirmButton } from "../Controls/ConfirmButton";
import { useRef } from "react";
import styled from "styled-components";
import { inputWithStyle } from "../../HOC/inputWithStyle";
import { inputWithFloatingLabel } from "../../HOC/inputWithFloatingLabel";
import InputBase from "../Inputs/InputBase";
import useArray from "../../utils/hooks/useArray";
import { FILTERS } from "../../utils/constants/FILTERS";
import {
  useDealsContext,
  ACTIONS as DEAL_ACTIONS,
} from "../../context/DealsProvider";
import ExtendDealsModal from "../Modals/ExtendDeals/ExtendDealsModal";
import colors from "../../utils/constants/COLORS";
import ViewAndEditModal from "../Modals/ViewAndEdit/ViewAndEditModal";
import {
  CONDITIONS,
  DEAL_EDIT_ACTIONS,
  IMAGES_ENDPOINT,
  USER_KEY,
} from "../../utils/constants/SYSTEM";
import {
  ALERT_ACTIONS,
  ALERT_STATUSES,
  useAlert,
} from "../../context/AlertProvider";
import api from "../../api/api";
import { get } from "../../utils/helpers/storage";
import parseDecimal from "../../utils/helpers/parseDecimal";
import { convertJsonToExcel } from "../../utils/helpers/excelToJson";
import useToast, { TOAST_TYPES } from "../../utils/hooks/useToast";
import { NavLink } from "react-router-dom";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const StyledTableCell = styled(TableCell)`
  span {
    display: block;
    width: max-content;
    font-size: x-small;
    color: GrayText;
  }
`;

const FilterPopupBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 560px;
  padding: 2px 1px;
`;

const FilterCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  padding: 10px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FilterCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FilterCardLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const FilterFieldsRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const FilterFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding-left: 2px;
`;

const AddFilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: none;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 9px 14px;
  color: ${colors.informational.color};
  cursor: pointer;
  font-size: 13px;
  width: 100%;
  transition: background 120ms ease, border-color 120ms ease;
  &:hover {
    background: ${colors.informational.background};
    border-color: ${colors.informational.stroke};
  }
`;

const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${colors.informational.background};
  color: ${colors.informational.color};
  border: 1px solid ${colors.informational.stroke};
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 7px;
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  color: ${colors.critical.color};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover {
    background: ${colors.critical.background};
  }
`;

const QuickFilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 14px;
  padding: 3px 11px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 120ms ease;
  border: 1px solid ${(props) => props.active ? "rgba(2, 136, 209, 0.5)" : "#d1d5db"};
  background: ${(props) => props.active ? "#2c6ecb" : "#fff"};
  color: ${(props) => props.active ? "#fff" : colors.secondary};
  &:hover {
    border-color: rgba(2, 136, 209, 0.5);
    background: #2c6ecb;
    color: #fff;
  }
`;

const QuickFiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-right: 4px;
`;

// ─── Quick filter presets ───────────────────────────────────────────────────
// Add, remove, or edit entries here to update available quick filters.
const QUICK_FILTERS = [
  {
    id: "on_carter_website",
    label: "On Carter Website",
    filters: [
      { label: "active_Deal", operator: "equal to", value: "yes" },
      { label: "display_On_Website", operator: "equal to", value: "yes" },
    ],
  },
];
// ────────────────────────────────────────────────────────────────────────────

function EnhancedTableHead(props) {
  const {
    headers,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    pushEnd,
    selected,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  console.log('selected', selected);

  return (
    <TableHead>
      <TableRow>
        <TableCell>
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all deals",
            }}
          />
        </TableCell>
        <TableCell sx={{ fontSize: "x-small", color: "GrayText" }}>
          {numSelected < 2 ? "Edit" : numSelected > 1 && "Bulk edit"}
          {numSelected > 0 && (
            <Tooltip title="Edit selected deal(s)">
              <IconButton
                onClick={() => pushEnd(<UpdateDealModal items={selected} scopeID={"used_Car_ID"} />)}
              >
                <Pencil weight="thin" size={14} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <StyledTableCell>
          <span>View & Edit</span>
        </StyledTableCell>
        <StyledTableCell>
          <span>Link</span>
        </StyledTableCell>
        {headers?.map((header) => (
          <StyledTableCell
            key={header.id}
            padding={"checkbox"}
            sortDirection={orderBy === header.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === header.id}
              direction={orderBy === header.id ? order : "asc"}
              onClick={createSortHandler(header.id)}
              sx={{ textTransform: "capitalize" }}
            >
              {orderBy === header.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
              {header.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  headers: PropTypes.array.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const InputWithFloatingLabel = inputWithStyle(
  inputWithFloatingLabel(InputBase)
);

const EnhancedTableToolbar = (props) => {
  const { numSelected, headers } = props;
  const {
    array: filtersList,
    setArray: setFiltersList,
    update: updateFiltersList,
    remove: removeFiltersList,
  } = useArray([{ label: "", operator: "contains", value: "" }]);

  const [showFilters, setShowFilters] = useType("boolean", false);
  const [{ dealsFiltered, dealRows, filtersList: activeFiltersList, quickFilters, condition }, dispatchDeals] = useDealsContext();
  const activeQuickFilterId = QUICK_FILTERS.find(
    (qf) => JSON.stringify(qf.filters) === JSON.stringify(quickFilters)
  )?.id ?? null;

  const handleQuickFilter = (qf) => {
    const next = activeQuickFilterId === qf.id ? [] : qf.filters;
    dispatchDeals({ type: DEAL_ACTIONS.SET_QUICK_FILTERS, payload: next });
  };

  const anchorRef = useRef();

  const NULL_OPERATORS = ["is empty", "is not empty"];
  const isFilterMissing =
    filtersList.filter((f) => !f.label || !f.operator || (!f.value && !NULL_OPERATORS.includes(f.operator))).length > 0;

  return (
    <Toolbar
      sx={{
        minHeight: "unset !important",
        pl: { sm: 2 },
        pr: { sm: 2 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Deals {numSelected > 0 && <>({numSelected}) selected</>} {activeFiltersList?.length > 0 && <>Filtered</>}
        {dealRows?.length > 1 && (
          <IconButton title={`Bulk download ${condition} deals (${dealRows?.length})`} onClick={() => convertJsonToExcel(dealRows, `Deal_Portal_Bulk_${condition}`, condition)}>
            <Download weight="thin" size={18} />
          </IconButton>
        )}
      </Typography>

      <QuickFiltersRow>
        {QUICK_FILTERS.map((qf) => (
          <QuickFilterChip
            key={qf.id}
            active={activeQuickFilterId === qf.id}
            onClick={() => handleQuickFilter(qf)}
          >
            {qf.label}
          </QuickFilterChip>
        ))}
      </QuickFiltersRow>

      {headers?.length > 0 && (
        <>
          <Tooltip title="Filter deals">
            <IconButton ref={anchorRef} onClick={() => setShowFilters(true)}>
              <Badge
                badgeContent={activeFiltersList?.length || 0}
                color="primary"
                invisible={!activeFiltersList?.length}
                sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 16, height: 16 } }}
              >
                <FunnelSimple
                  weight={activeFiltersList?.length > 0 ? "fill" : "thin"}
                  size={20}
                  color={activeFiltersList?.length > 0 ? colors.informational.color : colors.secondary}
                />
              </Badge>
            </IconButton>
          </Tooltip>
          <Popup
            open={showFilters}
            anchor={anchorRef.current}
            title={
              <QueueStyle>
                <QueueStyle gap="8px" width="auto">
                  <h5 style={{ margin: 0, fontSize: 14 }}>Filters</h5>
                  {activeFiltersList?.length > 0 && (
                    <ActiveBadge>{activeFiltersList.length} active</ActiveBadge>
                  )}
                </QueueStyle>
                {activeFiltersList?.length > 0 && (
                  <ClearAllButton
                    onClick={() => {
                      setFiltersList([{ label: "", operator: "contains", value: "" }]);
                      dispatchDeals({ type: DEAL_ACTIONS.SET_FILTERS_LIST, payload: [] });
                    }}
                  >
                    Clear all
                  </ClearAllButton>
                )}
              </QueueStyle>
            }
            body={
              <FilterPopupBody>
                {filtersList.map((f, index) => {
                  const isNullOp = NULL_OPERATORS.includes(f.operator);
                  return (
                    <FilterCard key={index}>
                      <FilterCardHeader>
                        <FilterCardLabel>Filter {index + 1}</FilterCardLabel>
                        {filtersList.length > 1 && (
                          <Tooltip title="Remove filter">
                            <IconButton size="small" onClick={() => removeFiltersList(index)}>
                              <X weight="thin" size={14} color={colors.critical.color} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </FilterCardHeader>
                      <FilterFieldsRow>
                        <FilterFieldGroup>
                          <FieldLabel htmlFor={`filter-item-${index + 1}`}>Column</FieldLabel>
                          <Select
                            options={headers.map((h) => h.label)}
                            value={f.label ? (headers.find((h) => h.id === f.label)?.label ?? "") : ""}
                            handleChange={(label) => {
                              const header = headers.find((h) => h.label === label);
                              updateFiltersList(index, {
                                ...f,
                                label: header ? header.id : label.replace(/\s/g, "_"),
                              });
                            }}
                            id={`filter-item-${index + 1}`}
                            width="200px"
                          />
                        </FilterFieldGroup>
                        <FilterFieldGroup>
                          <FieldLabel htmlFor={`filter-operator-${index + 1}`}>Operator</FieldLabel>
                          <Select
                            options={Object.keys(FILTERS)}
                            value={f.operator}
                            handleChange={(operator) =>
                              updateFiltersList(index, { ...f, operator })
                            }
                            id={`filter-operator-${index + 1}`}
                            width="170px"
                          />
                        </FilterFieldGroup>
                        {!isNullOp && (
                          <FilterFieldGroup>
                            <FieldLabel htmlFor={`filter-value-${index + 1}`}>Value</FieldLabel>
                            <InputWithFloatingLabel
                              value={f.value}
                              onChange={(e) =>
                                updateFiltersList(index, { ...f, value: e.target.value })
                              }
                              id={`filter-value-${index + 1}`}
                              width="170px"
                            />
                          </FilterFieldGroup>
                        )}
                      </FilterFieldsRow>
                    </FilterCard>
                  );
                })}
                <AddFilterButton
                  onClick={() =>
                    setFiltersList([...filtersList, { label: "", operator: "contains", value: "" }])
                  }
                >
                  <PlusCircle weight="thin" size={16} />
                  Add filter
                </AddFilterButton>
              </FilterPopupBody>
            }
            action={
              <>
                <CancelButton onClick={() => setShowFilters(false)} />
                <ConfirmButton
                  onClick={() => {
                    dispatchDeals({
                      type: DEAL_ACTIONS.SET_FILTERS_LIST,
                      payload: filtersList,
                    });
                    setShowFilters(false);
                  }}
                  disabled={isFilterMissing}
                />
              </>
            }
            variant="info"
            onClose={() => setShowFilters(false)}
          />
        </>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  headers: PropTypes.array.isRequired,
};

export default function TableCheckboxSelectUsed({ headers, rows }) {
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("list_Date");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const id = React.useId();

  const dealEditActions = Object.values(DEAL_EDIT_ACTIONS);

  const [{ isLoading, condition, dealsFiltered, filtersList: activeFilters, quickFilters: activeQuickFilters }, dispatchDeals] = useDealsContext();
  const displayRows = (activeFilters?.length > 0 || activeQuickFilters?.length > 0) ? dealsFiltered : rows;

  const [, dispatchModalHistory] = useModalHistory();
  
  const pushEnd = (modal) =>
    dispatchModalHistory({
      type: MODAL_ACTIONS.PUSH_END,
      payload: { value: modal },
    });
  const popEnd = () =>
    dispatchModalHistory({
      type: MODAL_ACTIONS.POP_END,
    });
  const [addToast] = useToast();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = displayRows.map((n) => n.used_Car_ID);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);

    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleDealUpdate = (value, row) => {
    switch (value) {
      case DEAL_EDIT_ACTIONS.TURN_ON:
        (async () => {
          const updateEndDateResp = await api.updateTurnDealOnOrOff(
            {
              ID: row.used_Car_ID,
              IsOn: true,
            }
          );

          if (updateEndDateResp?.success) {
            // dispatchDeals({ type: DEAL_ACTIONS.REFETCH_DEALS, payload: true });
            addToast(TOAST_TYPES.SUCCESS, "Successfully turned on deal.");
            insertDealAudit(row, "info", "Turned on (table action)", "", "", "");
          } else {
            addToast(TOAST_TYPES.ERROR, "Error turning on deal.");
          }
        })();
        break;
      case DEAL_EDIT_ACTIONS.TURN_OFF:
        (async () => {
          const updateEndDateResp = await api.updateTurnDealOnOrOff(
            {
              ID: row.used_Car_ID,
              IsOn: false,
            }
          );

          if (updateEndDateResp?.success) {
            // dispatchDeals({ type: DEAL_ACTIONS.REFETCH_DEALS, payload: true });
            addToast(TOAST_TYPES.SUCCESS, "Successfully turned off deal.");
            insertDealAudit(row, "info", "Turned off (table action)", "", "", "");
          } else {
            addToast(TOAST_TYPES.ERROR, "Error turning off deal.");
          }
        })();
        break;
      case DEAL_EDIT_ACTIONS.DUPLICATE:
        (async () => {
          const carDataPubInfo = await api.getUsedDealInfo(row?.used_Car_ID);
          console.log("carDataPubInfo", carDataPubInfo);
          const carDataPubData = {
            UserID: parseInt(get(USER_KEY)?.userID),

            ReconCost: parseDecimal(carDataPubInfo?.recon_Cost),
            ReconTime: parseDecimal(carDataPubInfo?.recon_Time),
            ActiveDeal: carDataPubInfo?.active_Deal,
            DisplayOnWebsite: carDataPubInfo?.display_On_Website,
            ActivelyAdvertise: carDataPubInfo?.actively_Advertise,
            MMCode: carDataPubInfo?.mM_Code,
            VinNumber: carDataPubInfo?.vin_Number,
            Brand: carDataPubInfo?.brand,
            Range: carDataPubInfo?.range,
            Model: carDataPubInfo?.model,
            ModelID: carDataPubInfo?.model_ID,
            SellingPrice: parseDecimal(carDataPubInfo?.selling_Price),
            StandInValue: parseDecimal(carDataPubInfo?.stand_In_Value),
            Mileage: parseDecimal(carDataPubInfo?.mileage),
            Colour: carDataPubInfo?.colour,
            ListDate: carDataPubInfo?.list_Date,
            FirstRegDate: carDataPubInfo?.first_Reg_Date,
            StockLocation: carDataPubInfo?.stock_Location,
            Condition: carDataPubInfo?.condition,
            Transmission: carDataPubInfo?.transmission,
            FuelType: carDataPubInfo?.fuel_Type,
            CashBack: parseDecimal(carDataPubInfo?.cash_Back),
            DealerSupport: parseDecimal(carDataPubInfo?.dealer_Support),
            Notes: carDataPubInfo?.notes,
            //UploadedPictures: "images",
            SearchBarcode: carDataPubInfo?.search_Barcode,
          };

          console.log("carDataPubData", carDataPubData);

          const insertCarDataPubResp = await api.insertCDPUsedDeal(
            carDataPubData
          );
          if (insertCarDataPubResp?.success) {
            addToast(TOAST_TYPES.SUCCESS, "Successfully duplicated deal.");

            const preloadedDealData = {
              UserID: parseInt(get(USER_KEY)?.userID),
              UsedCarID: `${insertCarDataPubResp?.id}`,
              StartDate: carDataPubInfo?.start_Date,
              EndDate: carDataPubInfo?.end_Date,
              AdvertisedPrice: parseDecimal(carDataPubInfo?.advertised_Price),
              RatePercent: parseDecimal(carDataPubInfo?.rate_Percent),
              Deposit: parseDecimal(carDataPubInfo?.deposit),
              Period: parseInt(carDataPubInfo?.period) || 0,
              BalloonPercent: parseDecimal(carDataPubInfo?.balloon_Percent),
              BalloonAmount: parseDecimal(carDataPubInfo?.balloon_Amount),
              TotalCostOfCredit: parseDecimal(
                carDataPubInfo?.total_Cost_Of_Credit
              ),
            };
            console.log("preloadedDealData", preloadedDealData);

            const insertPLDResp = await api.insertPLDUsedDeal(
              preloadedDealData
            );

            if (insertPLDResp?.success) {
              dispatchDeals({
                type: DEAL_ACTIONS.REFETCH_DEALS,
                payload: true,
              });
              addToast(TOAST_TYPES.SUCCESS, "Successfully duplicated deal.");
            } else {
              addToast(TOAST_TYPES.ERROR, "Error duplicating deal.");
            }
          } else {
            addToast(TOAST_TYPES.ERROR, "Error duplicating deal.");
          }
        })();
        break;
      case DEAL_EDIT_ACTIONS.EXTEND:
        pushEnd(
          <ExtendDealsModal
            onSubmit={(newDate) => {
              (async () => {
                const updateEndDateResp =
                  await api.updatePreloadedUsedDealEndDate({
                    ID: row.used_Car_ID,
                    EndDate: newDate,
                  });

                if (updateEndDateResp?.success) {
                  dispatchDeals({
                    type: DEAL_ACTIONS.REFETCH_DEALS,
                    payload: true,
                  });
                  addToast(TOAST_TYPES.SUCCESS, "Successfully updated end date.");
                } else {
                  addToast(TOAST_TYPES.ERROR, "Error updating end date.");
                }
              })();
              popEnd();
            }}
          />
        );
        break;

      default:
        break;
    }

    //fetchDeals(true);
  };

  const insertDealAudit = async (row, operation, change, oldValue, newValue, dealType) => {
    const dataMap = {
      PreloadedDealID: row.used_Car_ID,
      UserEmail: get(USER_KEY)?.emailAddress,
      Operation: operation,
      Change: change,
      OldValue: oldValue?.toString(),
      NewValue: newValue?.toString(),
      DealType: dealType
    }

    await api.insertDealAudit(dataMap);
  }

  const linkToCarterSite = (row) => `https://heycarter.co.za/cardetails/${encodeURIComponent(row?.brand)}/${encodeURIComponent(row?.range)}/${encodeURIComponent(row?.model)}%20(${encodeURIComponent(row?.transmission)}%20%7C%20${encodeURIComponent(row?.fuel_Type)})/${encodeURIComponent(row?.model_ID)}_used_${encodeURIComponent(row?.used_Car_ID)}`;
  const copyAndOpenLinkToCarterSite = async (row) => {
    // https://heycarter.co.za/cardetails/Ford/Ranger/2.0%20SiT%20double%20cab%20XL%20manual%20(manual%20%7C%20diesel)/FordRang4d20_used_1867
    await navigator.clipboard.writeText(linkToCarterSite(row));
  }

  // useEffect(() => {
  //   console.log("dealsResult", dealsResult);
  //   if (isFetchingDeals || hasNoValue(dealsResult)) return;
  //   dispatchDeals({ type: DEAL_ACTIONS.SET_DEALS, payload: dealsResult });
  // }, [isFetchingDeals, dealsResult, dispatchDeals]);

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - displayRows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <EnhancedTableToolbar numSelected={selected.length} headers={headers} />
        <LoadEffect
          condition={!isLoading}
          fallback={<StackStyle justifyContent="center"><CircularProgress size={30} /></StackStyle>}
        >
        <TableContainer sx={{display: "flex", flexGrow: 1}}>
            <ScrollEffect width="100%" height="calc(100vh - 253.38px)">
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="Deals"
              size="small"
              padding="checkbox"
              stickyHeader
            >
              <EnhancedTableHead
                headers={headers}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={displayRows.length}
                pushEnd={pushEnd}
                selected={selected}
              />

                <TableBody>
                  {stableSort(displayRows, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const isItemSelected = isSelected(row.used_Car_ID);
                      const labelId = `enhanced-table-checkbox-${index}`;
                      const filteredDealEditActions = [...dealEditActions];
                      const dealEditActionIndex =
                        filteredDealEditActions.indexOf(
                          DEAL_EDIT_ACTIONS.EXTEND
                        );
                      if (!row?.expiry_Date)
                        filteredDealEditActions.splice(dealEditActionIndex, 1);

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.used_Car_ID}
                          selected={isItemSelected}
                        >
                          <TableCell>
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              onClick={(event) =>
                                handleClick(event, row.used_Car_ID)
                              }
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <QueueStyle gap="10px">
                              <Select
                                defaultValue="Actions"
                                options={filteredDealEditActions}
                                handleChange={(value) =>
                                  handleDealUpdate(value, row)
                                }
                              />
                            </QueueStyle>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View & edit deal">
                              <NavLink to={`/in-stock/${row?.used_Car_ID}`}>
                                <IconButton
                                  sx={{width: "100%", height: "100%", padding: "16px !important"}}
                                >
                                  <Eye weight="thin" size={18} />
                                </IconButton>
                              </NavLink>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <a href={linkToCarterSite(row)} target="_blank" rel="noreferrer">
                              <IconButton sx={{width: "100%", height: "100%", padding: "16px !important"}} onClick={() => copyAndOpenLinkToCarterSite(row)}>
                                <Link weight="thin" size={18} />
                              </IconButton>
                            </a>
                          </TableCell>
                          {Object.keys(row).map((key, index) => (
                            <StyledTableCell
                              onClick={(event) =>
                                handleClick(event, row.used_Car_ID)
                              }
                              key={`${id}-${index}`}
                            >
                              {key === "image" ? <TableCell><img src={`${IMAGES_ENDPOINT}/${row?.image}`} alt="Car" height="80px" /></TableCell> : key === "notes" ? <p>{(row[key] === "" || row[key] === null) ? "" : row[key]?.slice(0, 24) + "..."}</p> : row[key]}
                            </StyledTableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: 33 * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
            </Table>
          </ScrollEffect>
        </TableContainer>
        </LoadEffect>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, displayRows?.length]}
          component="div"
          count={displayRows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
